import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import ClientFormDialog from './ClientFormDialog';
import { SectionCard, StatCard, EmptyState, number } from './ui';
import { CHART_COLORS, TRUE_COLORS } from '../theme';
import { Client } from '../types';

const WEEKS = 12;

export const clientInitials = (c: any) =>
  `${c?.name?.[0] ?? ''}${c?.lastname?.[0] ?? ''}`.toUpperCase() || '?';

const SearchToolbar = () => (
  <Box sx={{ px: 2, pt: 2, pb: 1 }}>
    <GridToolbarQuickFilter
      placeholder="Buscar por nombre, correo o teléfono…"
      debounceMs={200}
      sx={{ width: { xs: '100%', sm: 360 }, '& .MuiInputBase-root': { borderRadius: 2 } }}
    />
  </Box>
);

const Clients = () => {
  const axios = useAxios();
  const navigate = useNavigate();

  const [clients, setClients] = React.useState<Array<Client>>([]);
  const [receipts, setReceipts] = React.useState<Array<any>>([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const [c, r] = await Promise.allSettled([
        axios.get('api/clients/'),
        axios.get('api/receipts/', {
          params: {
            start_date: dayjs().subtract(WEEKS, 'week').startOf('week').utc().format(),
            end_date: dayjs().endOf('day').utc().format(),
            registered: true,
            compact: 1, // dates and clients are enough here; the items made this request 3 MB
          },
        }),
      ]);
      if (c.status === 'fulfilled') setClients(c.value.data ?? []);
      if (r.status === 'fulfilled') setReceipts(r.value.data ?? []);
      setLoading(false);
    })();
  }, [axios]);

  const monthAgo = dayjs().subtract(30, 'day');
  const newClients = clients.filter(c => dayjs((c as any).registry_date).isAfter(monthAgo)).length;
  const activeClients = new Set(
    receipts.filter(r => dayjs(r.purchase_date).isAfter(monthAgo)).map(r => r.client),
  ).size;
  const visits30 = receipts.filter(r => dayjs(r.purchase_date).isAfter(monthAgo)).length;

  const weekly = React.useMemo(() => {
    const starts = Array.from({ length: WEEKS }, (_, i) => dayjs().startOf('week').subtract(WEEKS - 1 - i, 'week'));
    const bucket = (date: string) => starts.findIndex((s, i) => {
      const d = dayjs(date);
      return !d.isBefore(s) && (i === WEEKS - 1 || d.isBefore(starts[i + 1]));
    });
    const visits = Array(WEEKS).fill(0);
    const signups = Array(WEEKS).fill(0);
    receipts.forEach(r => { const i = bucket(r.purchase_date); if (i >= 0) visits[i]++; });
    clients.forEach((c: any) => { const i = bucket(c.registry_date); if (i >= 0) signups[i]++; });
    return { labels: starts.map(s => s.format('D MMM')), visits, signups };
  }, [receipts, clients]);

  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: 'Cliente',
      flex: 1.4,
      minWidth: 220,
      valueGetter: (_value, row: any) => `${row.name ?? ''} ${row.lastname ?? ''}`.trim(),
      renderCell: params => (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', height: '100%' }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700, bgcolor: 'rgba(111,137,148,0.18)', color: TRUE_COLORS.primary }}>
            {clientInitials(params.row)}
          </Avatar>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} noWrap>{params.value}</Typography>
        </Stack>
      ),
    },
    { field: 'email', headerName: 'Correo', flex: 1.2, minWidth: 200, valueFormatter: v => v || '—' },
    { field: 'phone', headerName: 'Teléfono', flex: 0.8, minWidth: 140, valueFormatter: v => v || '—' },
    {
      field: 'registry_date', headerName: 'Cliente desde', flex: 0.8, minWidth: 130,
      valueGetter: v => (v ? new Date(v) : null), type: 'date',
      valueFormatter: (v: Date | null) => (v ? dayjs(v).format('D MMM YYYY') : '—'),
    },
    {
      field: 'last_visit', headerName: 'Última visita', flex: 0.8, minWidth: 130,
      valueGetter: v => (v ? new Date(v) : null), type: 'date',
      valueFormatter: (v: Date | null) => (v ? dayjs(v).fromNow() : '—'),
    },
  ];

  const chartOptions = {
    chart: { fontFamily: 'Inter, sans-serif', toolbar: { show: false }, zoom: { enabled: false } },
    colors: [CHART_COLORS[0], CHART_COLORS[2]],
    stroke: { curve: 'smooth' as const, width: [2.5, 2.5] },
    dataLabels: { enabled: false },
    markers: { size: 3, strokeWidth: 0 },
    grid: { borderColor: TRUE_COLORS.borderSolid, strokeDashArray: 4 },
    legend: { position: 'top' as const, horizontalAlign: 'left' as const },
    xaxis: { categories: weekly.labels, labels: { style: { colors: TRUE_COLORS.secondary } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v: number) => number(v), style: { colors: TRUE_COLORS.secondary } } },
    tooltip: { shared: true, intersect: false },
  };

  return (
    <PageShell
      title="Clientes"
      subtitle="Conoce a quienes te compran y cada cuánto regresan"
      actions={
        <Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setCreating(true)}>
          Agregar cliente
        </Button>
      }
    >
      <Grid container spacing={3}>
        {[
          { label: 'Clientes registrados', value: number(clients.length), icon: <PeopleAltOutlinedIcon /> },
          { label: 'Nuevos (30 días)', value: number(newClients), icon: <FiberNewOutlinedIcon /> },
          { label: 'Activos (30 días)', value: number(activeClients), icon: <HowToRegOutlinedIcon /> },
          { label: 'Visitas (30 días)', value: number(visits30), icon: <StorefrontOutlinedIcon /> },
        ].map(card => (
          <Grid key={card.label} size={{ xs: 6, sm: 6, lg: 3 }}>
            <StatCard label={card.label} value={card.value} icon={card.icon} loading={loading} />
          </Grid>
        ))}

        <Grid size={12}>
          <SectionCard title="Visitas y registros" subtitle={`Por semana, últimas ${WEEKS} semanas`}>
            <Chart
              type="line"
              height={260}
              options={chartOptions}
              series={[{ name: 'Visitas', data: weekly.visits }, { name: 'Nuevos clientes', data: weekly.signups }]}
            />
          </SectionCard>
        </Grid>

        <Grid size={12}>
          <SectionCard title="Directorio" subtitle={`${number(clients.length)} clientes`} noPadding>
            <DataGrid
              rows={clients}
              columns={columns}
              loading={loading}
              autoHeight
              rowHeight={56}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                sorting: { sortModel: [{ field: 'last_visit', sort: 'desc' }] },
              }}
              pageSizeOptions={[10, 25, 50]}
              onRowClick={params => navigate(`/admin/clients/${params.row.id}`)}
              slots={{
                toolbar: SearchToolbar,
                noRowsOverlay: () => (
                  <EmptyState
                    compact
                    title="Aún no tienes clientes"
                    description="Registra clientes desde TRUE POS al cobrar, o agrégalos aquí."
                  />
                ),
              }}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '--DataGrid-overlayHeight': '220px' }}
            />
          </SectionCard>
        </Grid>
      </Grid>

      <ClientFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={client => navigate(`/admin/clients/${client.id}`)}
      />
    </PageShell>
  );
};

export default Clients;
